import { useResolvedFilterSet } from '@/creditflowresearch/ui/common/hooks/filters/useResolvedFilterSet';
import { useFilterButtons, type IFilterSet } from '@/creditflowresearch/ui/common/hooks/filters/useFilterButtons';
import { ReactNode } from 'react';

interface IUseProductPageFiltersOptions {
  items?: ReactNode | IFilterSet[],
  isDisabled?: boolean,
  onToggle?: (searchQuery: string, filterName: string, queryName: string) => void,
}

interface IUseProductPageFiltersResult {
  filterSets: ReturnType<typeof useFilterButtons>['filterSets'],
  getActiveFilters: ReturnType<typeof useFilterButtons>['getActiveFilters'],
  getActiveItems: ReturnType<typeof useFilterButtons>['getActiveItems'],
  isFilterMode: boolean,
  renderItems: ReactNode | ReturnType<typeof useFilterButtons>['filterSets'],
}

const isFilterSetArray = (items: ReactNode | IFilterSet[] | undefined): items is IFilterSet[] => {
  if (!Array.isArray(items) || items.length === 0) {
    return false;
  }
  const firstItem = items[0];
  return typeof firstItem === 'object' && firstItem !== null && 'key' in firstItem;
};

export const useProductPageFilters = ({
  items,
  isDisabled = false,
  onToggle,
}: IUseProductPageFiltersOptions): IUseProductPageFiltersResult => {
  const isFilterMode = isFilterSetArray(items);
  const resolvedFilterSet = useResolvedFilterSet(isFilterMode ? items : []);

  const { filterSets, getActiveFilters, getActiveItems } = useFilterButtons({
    filterSet: resolvedFilterSet,
    isDisabled,
    onToggle,
  });

  return {
    filterSets,
    getActiveFilters,
    getActiveItems,
    isFilterMode,
    renderItems: isFilterMode ? filterSets : items,
  };
};
